import cn from 'classnames'
import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { useSelector } from 'react-redux'

import { Translation } from 'cng-common/features/translations/components/translation'

import { getCockpitPeriodLabel, getOfferingTitleKey } from '../../../lib'
import {
  selectCockpitCategory,
  selectCockpitCurrency,
  selectCockpitMetric,
  selectCockpitOffering,
  selectCockpitPeriods,
} from '../../../model'

import styles from './CockpitFilterSummary.module.scss'

type CockpitFilterSummaryProps = {
  expanded: boolean
  /** Whether the expanded state shows a divider below the summary, for when it's
   *  stacked above other body content. Default true; pass false when the summary
   *  sits inline elsewhere (e.g. a tile header) where a divider doesn't make sense. */
  bordered?: boolean
  offering?: boolean
  category?: boolean
  period?: boolean
  metric?: boolean
  currency?: boolean
}

export const CockpitFilterSummary = ({
  expanded,
  bordered = true,
  offering = false,
  category = false,
  period = false,
  metric = false,
  currency = false,
}: CockpitFilterSummaryProps) => {
  const selectedOffering = useSelector(selectCockpitOffering)
  const selectedCategory = useSelector(selectCockpitCategory)
  const selectedPeriods = useSelector(selectCockpitPeriods)
  const selectedMetric = useSelector(selectCockpitMetric)
  const selectedCurrency = useSelector(selectCockpitCurrency)
  const periodLabel = getCockpitPeriodLabel(selectedPeriods)
  const showCategory = category && Boolean(selectedCategory)
  const trailingContext = [
    period ? periodLabel : null,
    metric ? selectedMetric || '--' : null,
    currency ? selectedCurrency : null,
  ].filter((value): value is string => value !== null)
  const showTrailingContext = trailingContext.length > 0

  return (
    <div className={cn(styles.root, { [styles.expanded]: expanded && bordered })}>
      {offering && (
        <span className={styles.context}>
          <Translation text={getOfferingTitleKey(selectedOffering || ProgramTypes.Overall)} span />
        </span>
      )}
      {showCategory && selectedCategory && (
        <>
          {offering && (
            <span className={styles.separator} aria-hidden>
              &middot;
            </span>
          )}
          <span className={styles.context}>
            <Translation text={selectedCategory} span />
          </span>
        </>
      )}
      {showTrailingContext && (
        <>
          {(offering || showCategory) && (
            <span className={styles.separator} aria-hidden>
              &middot;
            </span>
          )}
          <span className={styles.context}>{trailingContext.join('·')}</span>
        </>
      )}
    </div>
  )
}

CockpitFilterSummary.displayName = 'CockpitFilterSummary'
